import { useState, useEffect, useRef } from "react";
import { cx } from "../utils";
import fullBodyReferenceImage from "../assets/full-body-reference.png";
import faceReferenceImage from "../assets/face-reference.png";
import toast from "react-hot-toast";
import { ReactComponent as AskEvenessIcon } from "./../assets/svgs/ask-eveness-icon.svg";
import { Link } from "react-router-dom";

function usePreviewUrl(file, fallback) {
  const [url, setUrl] = useState(fallback);

  useEffect(() => {
    if (!file) {
      setUrl(fallback);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file, fallback]);

  return url;
}

function UploadCard({
  title,
  subtitle,
  optionalText,
  accept = "image/png,image/jpeg",
  file,
  fallbackImage,
  inputRef,
  onChange,
  large = false,
  multiple = false,
  fileCount = 0,
  isReference = false,
  variant = "modal",
}) {
  const preview = usePreviewUrl(file, fallbackImage);
  const isDetails = variant === "details";
  const isHero = variant === "hero";

  return (
    <div
      className={cx(
        "relative overflow-hidden rounded-[5px] bg-[#121212]",
        isHero
          ? large
            ? "h-[257px]"
            : "h-[125px]"
          : isDetails
            ? large
              ? "h-[312px]"
              : "h-[151px]"
            : large
              ? "h-[340px]"
              : "h-[164px]",
      )}
    >
      {preview ? (
        <img
          src={preview}
          alt="Upload preview"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}

      <div className="absolute inset-0 bg-black/55" />

      {isReference ? (
        <div
          className={cx(
            "absolute z-10 font-spaceMono font-normal text-[#B5B5B5] text-[10px]",
            isDetails || isHero
              ? "left-2 top-2"
              : "left-3 top-3 mb-[4px]",
          )}
        >
          Reference
        </div>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={onChange}
      />

      <div className="absolute inset-0 flex flex-col items-center justify-center px-2 text-center text-white">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={cx(
            "flex items-center justify-center rounded-full border border-[#B8B8BB] bg-white text-[#4B4B4B] shadow-lg transition hover:scale-[1.03] mt-4",
            isHero
              ? "h-[30px] w-[30px] text-[22px] pb-1"
              : isDetails
                ? "h-[44px] w-[44px] text-[22px]"
                : "h-[48px] w-[49px] text-[24px]",          )}
          aria-label={title}
        >
          +
        </button>

        <div
          className={cx(
            "font-spaceMono font-bold uppercase text-white text-[10px]",
            isDetails || isHero
              ? "mt-2 leading-[13px] tracking-[0.08em]"
              : "mt-3 tracking-[6%]",
          )}
        >
          {title}
        </div>

        {optionalText ? (
          <div
            className={cx(
              "font-spaceMono font-bold text-white text-[10px] mt-[4px]",
              isDetails || isHero
                ? "leading-[13px] tracking-[0.08em]"
                : "tracking-[6%]",
            )}
          >
            {optionalText}
          </div>
        ) : null}

        <div
          className={cx(
            "mt-1 max-w-[145px] whitespace-pre-line font-spaceMono text-[#B8B8BB] text-[9px]",
            isDetails || isHero
              ? "leading-[12px] tracking-[0.05em]"
              : "tracking-[0.04em]",
          )}
        >
          {subtitle}
        </div>

        <div className={cx("font-spaceMono text-[9px] uppercase text-[#B8B8BB]", isHero ? "mt-[0px]" : "mt-2")}>
          JPG · PNG · UP TO 12MB
        </div>

        {multiple && fileCount > 0 ? (
          <div className="mt-2 rounded bg-white/90 px-2 py-1 font-spaceMono text-[8px] text-[#333]">
            {fileCount} angle{fileCount === 1 ? "" : "s"} selected
          </div>
        ) : null}
      </div>

      {file && large ? (
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between rounded-[4px] bg-[#232323]/95 px-3 py-2 font-spaceMono text-[8px] text-white">
          <span className="max-w-[65%] truncate">
            {file.name} · {(file.size / 1024 / 1024).toFixed(1)}MB
          </span>

          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="font-semibold hover:underline"
          >
            Try Again
          </button>
        </div>
      ) : null}
    </div>
  );
}

function MeasurementSlider({
  label,
  value,
  min,
  max,
  displayValue,
  onChange,
  variant = "modal",
}) {
  const isDetails = variant === "details";
  const isHero = variant === "hero";
  return (
    <label className="block">
      <span className={cx(
          "block font-spaceMono font-normal uppercase tracking-[0.02em] text-[#8A8580]",
          isHero ? "text-[10px]" : "text-[12px]",
        )}
      >
        {label}
      </span>
      <div
        className={cx(
          "grid grid-cols-[minmax(0,1fr)_auto] items-center",
          isHero
            ? "mt-[2px] gap-x-3"
            : isDetails
              ? "mt-[10px] gap-x-7"
              : "mt-[14px] gap-x-8",
        )}
      >
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={onChange}
          aria-label={label}
          className={cx(
            "h-5 w-full cursor-pointer appearance-none bg-transparent outline-none",
            "[&::-webkit-slider-runnable-track]:h-[4px]",
            "[&::-webkit-slider-runnable-track]:rounded-full",
            "[&::-webkit-slider-runnable-track]:bg-[#ECECEC]",
            "[&::-webkit-slider-thumb]:mt-[-8px]",
            "[&::-webkit-slider-thumb]:h-[20px]",
            "[&::-webkit-slider-thumb]:w-[20px]",
            "[&::-webkit-slider-thumb]:appearance-none",
            "[&::-webkit-slider-thumb]:rounded-full",
            "[&::-webkit-slider-thumb]:border-0",
            "[&::-webkit-slider-thumb]:bg-[#2B2B2B]",
            "[&::-webkit-slider-thumb]:shadow-none",
            "[&::-moz-range-track]:h-[4px]",
            "[&::-moz-range-track]:rounded-full",
            "[&::-moz-range-track]:border-0",
            "[&::-moz-range-track]:bg-[#ECECEC]",
            "[&::-moz-range-progress]:h-[4px]",
            "[&::-moz-range-progress]:rounded-full",
            "[&::-moz-range-progress]:bg-[#ECECEC]",
            "[&::-moz-range-thumb]:h-[20px]",
            "[&::-moz-range-thumb]:w-[20px]",
            "[&::-moz-range-thumb]:rounded-full",
            "[&::-moz-range-thumb]:border-0",
            "[&::-moz-range-thumb]:bg-[#2B2B2B]",
            "[&::-moz-range-thumb]:shadow-none",
          )}
        />
        <span className={cx(
            "whitespace-nowrap text-right font-spaceMono font-normal text-[#888888]",
            isHero ? "min-w-[62px] text-[10px]" : "min-w-[74px] text-[12px]",
          )}
        >
          {displayValue}
        </span>
      </div>
    </label>
  );
}

const ProfileMeasurementsForm = ({
  initialProfile,
  onAnalyze,
  onSignIn,
  showSignInText = true,
  containerClassName = "px-5 pb-7 pt-8 sm:px-10",
  variant = "modal",
  onTryOn,
  onSignUpLogin,
  children,
  hideAuthActions = false,
  isAuthenticated = false,
  heroTitle = "Upload Your Image to Access Eveness",
  submitLabel = "Start Eveness Survey",
}) => {
  const [fullBodyFile, setFullBodyFile] = useState(
    initialProfile?.fullBodyFile ?? null,
  );

  const [faceFile, setFaceFile] = useState(initialProfile?.faceFile ?? initialProfile?.faceFiles?.[0] ?? null);
  const [angleFiles, setAngleFiles] = useState(
    initialProfile?.angleFiles ?? [],
  );

  const [height, setHeight] = useState(initialProfile?.height ?? 170);
  const [weight, setWeight] = useState(initialProfile?.weight ?? 70);
  const [gender, setGender] = useState(initialProfile?.gender ?? "Female");

  const fullBodyInputRef = useRef(null);
  const faceInputRef = useRef(null);
  const anglesInputRef = useRef(null);
  const shouldHideAuthActions = hideAuthActions || isAuthenticated;
  const shouldShowSignInText = showSignInText && !isAuthenticated;

  useEffect(() => {
    setFullBodyFile(initialProfile?.fullBodyFile ?? null);

    setFaceFile(
      initialProfile?.faceFile ??
        initialProfile?.faceFiles?.[0] ??
        null
    );

    setAngleFiles(initialProfile?.angleFiles ?? []);
    setHeight(initialProfile?.height ?? 170);
    setWeight(initialProfile?.weight ?? 70);
    setGender(initialProfile?.gender ?? "Female");

    if (fullBodyInputRef.current) fullBodyInputRef.current.value = "";
    if (faceInputRef.current) faceInputRef.current.value = "";
    if (anglesInputRef.current) anglesInputRef.current.value = "";

  }, [initialProfile]);

  const handleAnalyze = () => {
    if (!fullBodyFile) {
      toast.error("Please add a full-body photo.");
      fullBodyInputRef.current?.click();
      return;
    }

    if (!faceFile) {
      toast.error("Please add a headshot.");
      faceInputRef.current?.click();
      return;
    }

    onAnalyze?.({
      fullBodyFile,
      faceFile,
      angleFiles,
      height,
      weight,
      gender,
    });
  };
  if (variant === "hero") {
    return (
      <div
        className="
          w-full rounded-[7px] border border-[#E4E1DE]
          bg-white p-[8px]
          shadow-[0_8px_30px_rgba(0,0,0,0.10)]
        "
      >
        <h2
          className="
            mb-[10px] max-w-[320px]
            font-helvena text-[22px] font-medium
            leading-[1.08] tracking-[-0.035em]
            text-[#111111]
            lg:text-[25px]
          "
        >
          {heroTitle}
        </h2>
        {/* Image uploads */}
        <div className="grid grid-cols-[1.65fr_1fr] gap-[7px]">
          <UploadCard
            large
            title="Add Bodyshot"
            subtitle={"Straight posture and\ntight clothing preferred"}
            isReference
            variant="hero"
            file={fullBodyFile}
            fallbackImage={fullBodyReferenceImage}
            inputRef={fullBodyInputRef}
            onChange={(event) => {
              setFullBodyFile(event.target.files?.[0] ?? null);
            }}
          />
          <div className="grid gap-[7px]">
            <UploadCard
              isReference
              title="Add Face"
              optionalText="(Headshot)"
              subtitle=""
              variant="hero"
              file={faceFile}
              fallbackImage={faceReferenceImage}
              inputRef={faceInputRef}
              onChange={(event) => {
                setFaceFile(event.target.files?.[0] ?? null);
              }}
            />
            <UploadCard
              title="More Angles"
              optionalText="(Optional up to 9)"
              subtitle=""
              file={null}
              variant="hero"
              fallbackImage={null}
              inputRef={anglesInputRef}
              multiple
              fileCount={angleFiles.length}
              onChange={(event) =>
                setAngleFiles(
                  Array.from(event.target.files ?? []).slice(0, 9),
                )
              }
            />
          </div>
        </div>
        {/* Measurements */}
        <div className="mt-1 space-y-[2px]">
          <MeasurementSlider
            label="Height"
            min={145}
            max={205}
            value={height}
            displayValue={`${height}cm  ${Math.floor(
              height / 30.48,
            )}′${Math.round((height / 2.54) % 12)}″`}
            onChange={(event) => setHeight(Number(event.target.value))}
            variant="hero"
          />
          <MeasurementSlider
            label="Weight"
            min={40}
            max={160}
            value={weight}
            displayValue={`${weight}kg  ${Math.round(
              weight * 2.20462,
            )}lb`}
            onChange={(event) => setWeight(Number(event.target.value))}
            variant="hero"
          />
        </div>
        {/* Gender */}
        <div className="mt-[8px] grid w-full grid-cols-[0.9fr_1.08fr_1.62fr_1fr] gap-[5px]">
          {["Male", "Female", "Non - Binary", "Other"].map((option) => {
            const isSelected = gender === option;

            return (
              <button
                key={option}
                type="button"
                onClick={() => setGender(option)}
                aria-pressed={isSelected}
                className={cx(
                  "flex h-[32px] min-w-0 items-center justify-center",
                  "whitespace-nowrap rounded-[5px] border px-1",
                  "font-spaceMono text-[9px] md:text-[11px] font-normal",
                  "transition-colors duration-150",
                  isSelected
                    ? "border-[#8F333B] bg-[#8F333B] text-white"
                    : "border-[#D9D5D0] bg-white text-[#3D3A37] hover:border-[#B9B4AE]",
                )}
              >
                {option}
              </button>
            );
          })}
        </div>
        {/* Start survey */}
        <button
          type="button"
          onClick={handleAnalyze}
          className="
            relative mt-[8px] flex h-[34px] w-full
            items-center justify-center rounded-[5px]
            bg-[#313131] px-4
            font-spaceMono text-[11px]
            text-white tracking-[10%]
            transition hover:bg-[#252525]
          "
        >
          <span>{submitLabel}</span>
          <span className="ml-2">
            <AskEvenessIcon/>
          </span>
        </button>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E6E4E0] rounded-[5px] p-[10px] mt-[10px]">
    <div
      className={cx(
        containerClassName,
        variant === "details" &&
          "rounded-[5px] bg-[#F4F2EE] px-[8px] pb-[9px] pt-[10px] sm:px-[10px] border-[#F4F2EE]",
      )}
    >
      {variant === "details" ? (
        <div className="mx-[10px] sm:mx-[20px]">
          <p className="font-spaceMono text-[10px] tracking-[0.22em] text-[#7E2340] sm:text-[10px]">
            {"// A Brand That Helps You Find What Works"}
          </p>

          <div className="mt-[10px] rounded-t-[8px] bg-white px-[10px] pb-[11px] pt-[11px] sm:px-[20px]">
            <h2 className="font-helvena text-[18px] font-medium text-[#000000]">
              See How Well This Suits You
            </h2>

            <p className="mt-[8px] font-spaceMono text-[10px] text-[#000000] sm:text-[12px]">
              <strong>Upload a few photos</strong> to get your personalized
              Match Score, recommended size, and{" "}
              <strong>see how this piece works</strong> with your:
              <br />
              <strong>Body shape · proportions · coloring · features</strong>
            </p>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <h2 className="font-helvena text-[24px] font-medium tracking-[-2%] text-[#313131]">
            Add Measurements & Pictures
          </h2>
          <p className="mx-auto mt-2 max-w-[400px] font-helvena font-normal text-[12px] leading-5 text-[#8A8580]">
            Upload a few photos and add your measurements. We analyze your
            proportions, face shape, and color analysis.
          </p>
        </div>
      )}

      <div
        className={
          variant === "details"
            ? "bg-white px-[10px] sm:px-[18px] mx-[10px] sm:mx-[20px] rounded-b-[5px]"
            : ""
        }
      >
        <div
          className={cx(
            "mx-auto grid grid-cols-[1.65fr_1fr]",
            variant === "details"
              ? "max-w-none gap-[10px]"
              : "mt-4 max-w-[410px] gap-2.5",
          )}
        >
          {" "}
          <UploadCard
            large
            title="Add Bodyshot"
            subtitle={"Straight posture and tight clothing preffered"}
            isReference={true}
            variant={variant}
            file={fullBodyFile}
            fallbackImage={fullBodyReferenceImage}
            inputRef={fullBodyInputRef}
            onChange={(event) => {
              setFullBodyFile(event.target.files?.[0] ?? null);
            }}
          />
          <div className="grid gap-2.5">
            <UploadCard
              optionalText="(Required)"
              isReference={true}
              title={"Add Headshot"}
              variant={variant}
              file={faceFile}
              fallbackImage={faceReferenceImage}
              inputRef={faceInputRef}
              onChange={(event) => {
                setFaceFile(event.target.files?.[0] ?? null);
              }}
            />
            <UploadCard
              title="MORE ANGLES"
              optionalText="(Optional up to 9)"
              subtitle=""
              file={null}
              variant={variant}
              fallbackImage={null}
              inputRef={anglesInputRef}
              multiple
              fileCount={angleFiles.length}
              onChange={(event) =>
                setAngleFiles(Array.from(event.target.files ?? []).slice(0, 9))
              }
            />
          </div>
        </div>

        <div
          className={cx(
            "mx-auto",
            variant === "details"
              ? "mt-[10px] max-w-none"
              : "mt-3 max-w-[415px]",
          )}
        >
          {" "}
          <div
            className={
              variant === "details" ? "space-y-[8px]" : "space-y-[10px]"
            }
          >
            <MeasurementSlider
              label="Height"
              min={145}
              max={205}
              value={height}
              displayValue={`${height}cm  ${Math.floor(
                height / 30.48,
              )}′${Math.round((height / 2.54) % 12)}″`}
              onChange={(event) => setHeight(Number(event.target.value))}
              variant={variant}
            />

            <MeasurementSlider
              label="Weight"
              min={40}
              max={160}
              value={weight}
              displayValue={`${weight}kg  ${Math.round(weight * 2.20462)}lb`}
              onChange={(event) => setWeight(Number(event.target.value))}
              variant={variant}
            />
          </div>
          <div
            className={cx(
              "grid w-full grid-cols-[0.9fr_1.08fr_1.62fr_1fr] gap-[6px]",
              variant === "details" ? "mt-[10px]" : "mt-4",
            )}
          >
            {["Male", "Female", "Non - Binary", "Other"].map((option) => {
              const isSelected = gender === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => setGender(option)}
                  aria-pressed={isSelected}
                  className={cx(
                    "flex min-w-0 items-center justify-center",
                    variant === "details" ? "h-[38px]" : "h-[42px]",
                    "whitespace-nowrap rounded-[5px] border",
                    "px-2 font-spaceMono font-normal",
                    variant === "details" ? "text-[10px]" : "text-[12px]",
                    "transition-colors duration-150",
                    isSelected
                      ? "border-[#8F333B] bg-[#8F333B] text-white"
                      : "border-[#D9D5D0] bg-white text-[#3D3A37] hover:border-[#B9B4AE]",
                  )}
                >
                  {option}
                </button>
              );
            })}
          </div>
          {variant === "details" ? (
            <>
            <div className={cx( "-mx-[18px] mt-[9px] bg-[#F4F1EC] px-[8px] pb-[8px] pt-[8px] text-center font-helvena text-[10px] leading-[14px] text-[#8A8580]", 
            shouldHideAuthActions
              ? "flex justify-center"
              : "grid grid-cols-[1fr_1.08fr] gap-[19px]"
                )}
              >
                <button
                  type="button"
                  onClick={handleAnalyze}
                  className={cx(
                    "flex h-[38px] items-center justify-center rounded-[5px]",
                    "bg-[#8F333B] font-spaceMono text-[11px] font-bold",
                    "tracking-[0.18em] text-white",
                    shouldHideAuthActions ? "w-full max-w-[220px]" : "w-full"
                  )}
                >
                  Continue
                </button>
              {!hideAuthActions && (
                <button
                  type="button"
                  onClick={onSignUpLogin}
                  className="flex h-[38px] items-center justify-center gap-4 rounded-[5px] bg-[#313131] font-spaceMono text-[11px] tracking-[0.18em] text-white px-2 whitespace-nowrap"
                >
                  Sign UP / Log IN <span> <AskEvenessIcon /></span>
                </button>
              )}
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={handleAnalyze}
              className={cx(
                "mt-4 flex h-[42px] w-full items-center justify-center",
                "rounded-[5px]",
                "bg-[linear-gradient(100deg,#ECB1A4_0%,#D26068_62%,#C68D95_100%)]",
                "font-helvena text-[14px] font-normal text-white",
                "transition hover:brightness-[0.98]",
              )}
            >
              Analyze Profile
            </button>
          )}
          {shouldShowSignInText && variant !== "details" ? (
            <p className="mt-4 text-center font-helvena text-[12px] font-normal text-[#8A8580]">
              No credit card required&nbsp;&nbsp;•&nbsp;&nbsp;Already have an
              account?{" "}
              <button
                type="button"
                onClick={onSignIn}
                className="font-semibold text-[#282828] hover:underline"
              >
                Sign In.
              </button>
            </p>
          ) : null}
        </div>
      </div>
    </div>
      {
        variant === "details" &&
        <p className="mt-[10px] bg-white px-[8px] pb-[1px] pt-[1px] text-center font-helvena text-[12px] text-[#8A8580]">
          By continuing, you agree to our{" "}
          <Link to="/policies/privacy-policy" className="underline">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link to="/policies/terms-of-service" className="underline">
            Terms
          </Link>
          {!shouldHideAuthActions && (
          <>
            <br />
            Already have an account?{" "}
            <button
              type="button"
              onClick={onSignUpLogin}
              className="font-bold text-[#282828]"
            >
              Sign In.
            </button>
            </>
          )}
        </p>

      }
    </div>
  );
};

export default ProfileMeasurementsForm;
